/**
 * Units Cache Hook with SWR Pattern
 *
 * Provides instant loading from localStorage cache while revalidating
 * in the background. This significantly improves perceived performance
 * for the /booking/lodges/ page.
 *
 * SWR = Stale-While-Revalidate
 */

'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { listUnits, Unit, ListUnitsQuery } from '@/lib/admin/units-service';

const CACHE_KEY = 'apex_units_cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface CacheData {
  units: Unit[];
  timestamp: number;
  query: string;
}

/**
 * Get cached units from localStorage
 */
function getFromCache(queryKey: string): Unit[] | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const data: CacheData = JSON.parse(raw);

    // Check if cache is for the same query
    if (data.query !== queryKey) return null;

    // Check if cache is expired
    if (Date.now() - data.timestamp > CACHE_TTL) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    return data.units;
  } catch {
    // Invalid cache, remove it
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch {
      // Ignore localStorage errors
    }
    return null;
  }
}

/**
 * Save units to localStorage cache
 */
function saveToCache(units: Unit[], queryKey: string): void {
  if (typeof window === 'undefined') return;

  try {
    const data: CacheData = {
      units,
      timestamp: Date.now(),
      query: queryKey,
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // localStorage full or not available - ignore
  }
}

interface UseUnitsResult {
  units: Unit[];
  loading: boolean;
  error: string | null;
  isStale: boolean;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching units with SWR caching pattern
 *
 * @example
 * const { units, loading, isStale, refetch } = useUnitsWithCache({
 *   active: 'true',
 *   sortBy: 'sortOrder',
 *   sortDir: 'asc',
 * });
 */
export function useUnitsWithCache(query: ListUnitsQuery = {}): UseUnitsResult {
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isStale, setIsStale] = useState(false);
  const fetchedRef = useRef(false);

  // Stable query key for cache
  const queryKey = JSON.stringify(query);

  const fetchUnits = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);

      const fetchedUnits = await listUnits(query);

      // Save to cache
      saveToCache(fetchedUnits, queryKey);

      setUnits(fetchedUnits);
      setIsStale(false);
    } catch (err) {
      console.error('Error fetching units:', err);
      setError(err instanceof Error ? err.message : 'Failed to load accommodations');
    } finally {
      setLoading(false);
    }
  }, [queryKey, query]);

  useEffect(() => {
    // Prevent double fetch in StrictMode
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    // 1. Try to get from cache first (instant)
    const cached = getFromCache(queryKey);

    if (cached && cached.length > 0) {
      // Show cached data immediately
      setUnits(cached);
      setLoading(false);
      setIsStale(true);

      // 2. Revalidate in background (without showing loading state)
      fetchUnits(false);
    } else {
      // No cache, fetch normally with loading state
      fetchUnits(true);
    }
  }, [queryKey, fetchUnits]);

  return {
    units,
    loading,
    error,
    isStale,
    refetch: fetchUnits,
  };
}
